import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

interface RandomEventDef {
  id: string;
  title: string;
  description: string;
  weight: number;
  minYear: number;
  minStaff: number;
  cooldownWeeks: number;
  choices?: { label: string; effect: (state: SimulationState, rng: SeededRandom) => string }[];
  effect?: (state: SimulationState, rng: SeededRandom) => void;
}

const RANDOM_EVENTS: RandomEventDef[] = [
  {
    id: 'power_outage', title: 'Power Outage!',
    description: 'A power surge knocked out your office for the day. Some work was lost.',
    weight: 3, minYear: 1, minStaff: 1, cooldownWeeks: 20,
    effect: (state) => {
      for (const p of state.projects) {
        if (p.phase !== 'complete' && p.phase !== 'ready') p.bugs += 3;
      }
    },
  },
  {
    id: 'game_convention', title: 'Game Convention!',
    description: 'A major game convention is coming up. Send a team for a reputation boost?',
    weight: 2, minYear: 2, minStaff: 2, cooldownWeeks: 52,
    choices: [
      { label: 'Attend ($2,000)', effect: (state) => {
        if (state.company.funds >= 2000) {
          state.company.funds -= 2000;
          state.company.totalExpenses += 2000;
          state.company.reputation = Math.min(100, state.company.reputation + 8);
          state.company.fans += 500;
          return 'Great showing! +8 reputation, +500 fans.';
        }
        return "Can't afford to attend.";
      }},
      { label: 'Skip it', effect: () => 'You stayed home.' },
    ],
  },
  {
    id: 'viral_video', title: 'Viral Video!',
    description: 'A streamer featured one of your games and it went viral!',
    weight: 1, minYear: 2, minStaff: 1, cooldownWeeks: 40,
    effect: (state) => {
      state.company.fans += Math.round(1000 + state.company.fans * 0.1);
      state.company.reputation = Math.min(100, state.company.reputation + 5);
    },
  },
  {
    id: 'staff_poaching', title: 'Poaching Attempt!',
    description: 'A rival studio is trying to poach one of your staff with a big salary offer.',
    weight: 3, minYear: 3, minStaff: 3, cooldownWeeks: 30,
    choices: [
      { label: 'Match the offer (+30% salary)', effect: (state, rng) => {
        const targets = state.staff.filter(s => !s.isCEO && s.level >= 5);
        if (targets.length === 0) return 'No senior staff to poach.';
        const target = rng.pick(targets);
        target.salary = Math.round(target.salary * 1.3);
        target.satisfaction = Math.min(100, target.satisfaction + 20);
        return `${target.name} stays — salary raised to $${target.salary}/wk.`;
      }},
      { label: 'Let them go', effect: (state, rng) => {
        const targets = state.staff.filter(s => !s.isCEO && s.level >= 5);
        if (targets.length === 0) return 'No senior staff to poach.';
        const target = rng.pick(targets);
        state.staff = state.staff.filter(s => s.id !== target.id);
        for (const t of state.teams) {
          t.memberIds = t.memberIds.filter(id => id !== target.id);
          if (t.leaderId === target.id) t.leaderId = null;
        }
        return `${target.name} left for the rival studio.`;
      }},
    ],
  },
  {
    id: 'tax_rebate', title: 'Tax Rebate!',
    description: 'The government announced a tax rebate for game development studios.',
    weight: 2, minYear: 2, minStaff: 1, cooldownWeeks: 52,
    effect: (state) => {
      const rebate = Math.round(Math.max(1000, state.company.totalExpenses * 0.03));
      state.company.funds += rebate;
    },
  },
  {
    id: 'office_flood', title: 'Office Flood!',
    description: 'A pipe burst and flooded part of the office. Repair costs and lost productivity.',
    weight: 2, minYear: 1, minStaff: 2, cooldownWeeks: 40,
    effect: (state) => {
      const cost = Math.round(500 + state.office.level * 500);
      state.company.funds -= cost;
      state.company.totalExpenses += cost;
      for (const s of state.staff) s.energy = Math.max(0, s.energy - 20);
    },
  },
  {
    id: 'morale_boost', title: 'Team Outing!',
    description: 'The team decided to go out for dinner. Everyone feels refreshed!',
    weight: 3, minYear: 1, minStaff: 2, cooldownWeeks: 20,
    effect: (state) => {
      const cost = state.staff.length * 50;
      state.company.funds -= cost;
      state.company.totalExpenses += cost;
      for (const s of state.staff) {
        s.morale = Math.min(100, s.morale + 15);
        s.energy = Math.min(100, s.energy + 10);
      }
    },
  },
  {
    id: 'bug_discovery', title: 'Critical Bug Found!',
    description: 'QA discovered a critical bug in your current project. It needs immediate attention.',
    weight: 3, minYear: 1, minStaff: 1, cooldownWeeks: 15,
    effect: (state) => {
      const active = state.projects.find(p => p.phase !== 'complete' && p.phase !== 'ready');
      if (active) active.bugs += 10;
    },
  },
  {
    id: 'inspiration', title: 'Creative Breakthrough!',
    description: 'One of your developers had a brilliant idea. Quality boost on current project!',
    weight: 2, minYear: 1, minStaff: 1, cooldownWeeks: 25,
    effect: (state) => {
      const active = state.projects.find(p => p.phase !== 'complete' && p.phase !== 'ready');
      if (active) {
        for (const stat of Object.keys(active.progress)) {
          active.progress[stat] = (active.progress[stat] ?? 0) * 1.05;
        }
      }
    },
  },
  {
    id: 'hardware_failure', title: 'Server Crash!',
    description: 'The build server crashed. You need to buy a replacement.',
    weight: 2, minYear: 3, minStaff: 3, cooldownWeeks: 40,
    choices: [
      { label: 'Buy new server ($3,000)', effect: (state) => {
        if (state.company.funds >= 3000) {
          state.company.funds -= 3000;
          state.company.totalExpenses += 3000;
          return 'New server installed. Back to work!';
        }
        return "Can't afford a replacement. Productivity will suffer.";
      }},
      { label: 'Use cloud ($500/week for 4 weeks)', effect: (state) => {
        state.company.funds -= 500;
        state.company.totalExpenses += 500;
        return 'Using cloud services temporarily.';
      }},
    ],
  },
  {
    id: 'industry_award', title: 'Industry Award Nomination!',
    description: 'Your latest game has been nominated for an industry award!',
    weight: 1, minYear: 3, minStaff: 3, cooldownWeeks: 52,
    effect: (state) => {
      state.company.reputation = Math.min(100, state.company.reputation + 10);
      state.company.fans += 2000;
    },
  },
  {
    id: 'intern_arrives', title: 'Intern Application',
    description: 'A promising intern wants to work for free for a few weeks. Accept?',
    weight: 2, minYear: 2, minStaff: 2, cooldownWeeks: 30,
    choices: [
      { label: 'Accept intern', effect: (state) => {
        for (const p of state.projects) {
          if (p.phase !== 'complete' && p.phase !== 'ready') {
            for (const stat of Object.keys(p.progress)) {
              p.progress[stat] = (p.progress[stat] ?? 0) + 50;
            }
          }
        }
        return 'The intern helped out! Small boost to all projects.';
      }},
      { label: 'Decline', effect: () => 'Maybe next time.' },
    ],
  },
  {
    id: 'market_crash', title: 'Market Downturn',
    description: 'The gaming market is in a slump. Sales across the industry are down.',
    weight: 1, minYear: 4, minStaff: 1, cooldownWeeks: 80,
    effect: (state) => {
      for (const p of state.products) {
        if (p.weeklySales.length > 0) {
          p.weeklySales[p.weeklySales.length - 1] = Math.round((p.weeklySales[p.weeklySales.length - 1] ?? 0) * 0.5);
        }
      }
    },
  },
  {
    id: 'platform_deal', title: 'Platform Exclusivity Offer',
    description: 'A platform holder offers $10,000 for a timed exclusivity deal on your next game.',
    weight: 1, minYear: 3, minStaff: 3, cooldownWeeks: 52,
    choices: [
      { label: 'Accept deal ($10,000)', effect: (state) => {
        state.company.funds += 10000;
        return 'Deal accepted! +$10,000. Your next game will have limited reach initially.';
      }},
      { label: 'Stay independent', effect: () => 'You kept your independence.' },
    ],
  },
  {
    id: 'coffee_spill', title: 'Coffee Disaster',
    description: 'Someone spilled coffee on a workstation. Minor setback.',
    weight: 4, minYear: 1, minStaff: 1, cooldownWeeks: 15,
    effect: (state, rng) => {
      const staff = state.staff.filter(s => !s.isCEO);
      if (staff.length > 0) {
        const victim = rng.pick(staff);
        victim.energy = Math.max(0, victim.energy - 30);
      }
    },
  },
  {
    id: 'press_interview', title: 'Press Interview Request',
    description: 'A gaming magazine wants to interview your CEO about the studio.',
    weight: 2, minYear: 2, minStaff: 1, cooldownWeeks: 30,
    choices: [
      { label: 'Do the interview', effect: (state) => {
        state.company.reputation = Math.min(100, state.company.reputation + 5);
        state.company.fans += 300;
        return 'Great publicity! +5 reputation, +300 fans.';
      }},
      { label: 'Too busy', effect: () => 'You declined the interview.' },
    ],
  },
  {
    id: 'supply_shortage', title: 'Hardware Supply Shortage',
    description: 'A global chip shortage is driving up equipment costs.',
    weight: 1, minYear: 3, minStaff: 3, cooldownWeeks: 52,
    effect: (state) => {
      for (const s of state.staff) {
        if (s.equipmentBonus > 1.0) {
          s.equipmentTicksRemaining = Math.round(s.equipmentTicksRemaining * 0.7);
        }
      }
    },
  },
  {
    id: 'surprise_hit', title: 'Surprise Hit!',
    description: 'One of your older games is suddenly trending again!',
    weight: 1, minYear: 3, minStaff: 1, cooldownWeeks: 52,
    effect: (state) => {
      if (state.products.length > 0) {
        const old = state.products[Math.floor(state.products.length / 2)];
        if (old) {
          const bonus = Math.round(500 + state.company.fans * 0.05);
          state.company.funds += bonus;
          state.company.totalRevenue += bonus;
          state.company.fans += 200;
        }
      }
    },
  },
  {
    id: 'team_conflict', title: 'Team Conflict',
    description: 'Two of your staff members are having a disagreement. It\'s affecting morale.',
    weight: 3, minYear: 1, minStaff: 3, cooldownWeeks: 20,
    choices: [
      { label: 'Mediate personally', effect: (state) => {
        for (const s of state.staff) s.morale = Math.min(100, s.morale + 5);
        return 'You resolved the conflict. Morale improved slightly.';
      }},
      { label: 'Let them work it out', effect: (state, rng) => {
        const staff = state.staff.filter(s => !s.isCEO);
        if (staff.length >= 2) {
          rng.pick(staff).morale = Math.max(0, rng.pick(staff).morale - 20);
        }
        return 'The conflict lingered. Someone\'s morale dropped.';
      }},
    ],
  },
  {
    id: 'lucky_bug', title: 'Happy Accident',
    description: 'A bug in your game turned into a beloved feature! Players love it.',
    weight: 1, minYear: 2, minStaff: 1, cooldownWeeks: 40,
    effect: (state) => {
      const active = state.projects.find(p => p.phase !== 'complete' && p.phase !== 'ready');
      if (active) {
        active.bugs = Math.max(0, active.bugs - 5);
        for (const stat of Object.keys(active.progress)) {
          active.progress[stat] = (active.progress[stat] ?? 0) * 1.03;
        }
      }
    },
  },
];

// Track last fire time per event
let eventCooldowns = new Map<string, number>();

/**
 * EventSystem handles scripted milestones and random events.
 */
export function eventSystemUpdate(
  state: SimulationState,
  themeData: ThemeData,
  rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  const shown = state.tutorialTipsShown;
  const ticksPerWeek = 70; // 10 ticks/day * 7 days

  // Reset cooldowns at game start
  if (state.company.currentTick <= 1) {
    eventCooldowns = new Map();
  }

  // ─── Platform lifecycle ────────────────────────────────

  for (const platform of themeData.entities.platforms) {
    const launchKey = `platform_launched_${platform.id}`;
    if (state.company.currentYear >= platform.releasedYear && !shown.includes(launchKey)) {
      shown.push(launchKey);
      events.push({ type: 'PLATFORM_LAUNCHED', platformId: platform.id, platformName: platform.name });
    }

    if (platform.retiredYear) {
      const retireKey = `platform_retired_${platform.id}`;
      if (state.company.currentYear >= platform.retiredYear && !shown.includes(retireKey)) {
        shown.push(retireKey);
        events.push({ type: 'PLATFORM_RETIRED', platformId: platform.id, platformName: platform.name });
      }
    }
  }

  // ─── Scripted milestones ───────────────────────────────

  if (state.company.totalProductsReleased === 1 && !shown.includes('milestone_first_release')) {
    shown.push('milestone_first_release');
    events.push({ type: 'SCRIPTED_EVENT', eventId: 'first_release', title: 'First Release!', description: 'Congratulations! You\'ve released your first product!' });
  }

  if (state.company.hallOfFameCount === 1 && !shown.includes('milestone_first_hof')) {
    shown.push('milestone_first_hof');
    events.push({ type: 'SCRIPTED_EVENT', eventId: 'first_hof', title: 'Hall of Fame!', description: 'Your game has been inducted into the Hall of Fame!' });
  }

  const revenueMilestones = [100000, 500000, 1000000, 5000000, 10000000];
  for (const milestone of revenueMilestones) {
    const key = `milestone_revenue_${milestone}`;
    if (state.company.totalRevenue >= milestone && !shown.includes(key)) {
      shown.push(key);
      events.push({ type: 'SCRIPTED_EVENT', eventId: key, title: 'Revenue Milestone!', description: `Total revenue has exceeded $${(milestone / 1000).toLocaleString()}K!` });
    }
  }

  // ─── Random events (check once per week) ──────────────

  if (state.company.currentTick % ticksPerWeek !== 0 || state.company.currentTick === 0) {
    return events;
  }

  // ~15% chance of a random event each week
  if (!rng.nextBool(0.15)) return events;

  // Filter eligible events
  const eligible = RANDOM_EVENTS.filter((e) => {
    if (state.company.currentYear < e.minYear) return false;
    if (state.staff.length < e.minStaff) return false;
    const lastFired = eventCooldowns.get(e.id) ?? 0;
    if (state.company.currentTick - lastFired < e.cooldownWeeks * ticksPerWeek) return false;
    return true;
  });

  if (eligible.length === 0) return events;

  // Weighted random pick
  const totalWeight = eligible.reduce((s, e) => s + e.weight, 0);
  let roll = rng.nextFloat(0, totalWeight);
  let picked: RandomEventDef | null = null;
  for (const e of eligible) {
    roll -= e.weight;
    if (roll <= 0) { picked = e; break; }
  }
  if (!picked) picked = eligible[eligible.length - 1]!;

  eventCooldowns.set(picked.id, state.company.currentTick);

  // Apply immediate effect (if no choices)
  if (!picked.choices && picked.effect) {
    picked.effect(state, rng);
  }

  if (picked.choices) {
    events.push({
      type: 'RANDOM_EVENT',
      eventId: picked.id,
      title: picked.title,
      description: picked.description,
      choices: picked.choices.map((c) => c.label),
    });
  } else {
    events.push({
      type: 'RANDOM_EVENT',
      eventId: picked.id,
      title: picked.title,
      description: picked.description,
    });
  }

  return events;
}

/** Process a player's choice for a random event with choices. */
export function handleEventChoice(
  eventId: string,
  choiceIndex: number,
  state: SimulationState,
  rng: SeededRandom,
): string {
  const def = RANDOM_EVENTS.find((e) => e.id === eventId);
  if (!def?.choices || choiceIndex < 0 || choiceIndex >= def.choices.length) return '';
  return def.choices[choiceIndex]!.effect(state, rng);
}
